/**
 * Asset classes — `/finance/asset-classes`.
 *
 * All four control accounts are required and must differ from one another: a
 * class with an unmapped account produces a depreciation run that either fails
 * at the ledger or posts somewhere arbitrary, so the incomplete class is
 * refused (`assets.validation_failed`) — and refused here first.
 *
 * Revising a class changes what FUTURE capitalizations inherit and nothing
 * else. Every asset carries its own copy of the method, life, factor and
 * salvage decided when it was capitalized, so a policy revision can never
 * silently rewrite the schedule of an asset already in service. `code` is
 * immutable because it is the identity assets were filed under.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox } from "@erp/ui";
import { Plus } from "lucide-react";

import { surfaceProblem } from "@/components/form";
import { useIdempotentMutation } from "@/features/shared/ops-api";
import { localizedName } from "@/lib/i18n";

import {
  DEPRECIATION_METHODS,
  createAssetClass,
  replaceAssetClass,
  type AssetClass,
  type AssetClassWrite,
  type DepreciationMethod,
} from "../api";
import { normalizeDecimal } from "../decimal";
import {
  DecimalField,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import { financeKeys, useAssetClasses, usePostableAccounts } from "../queries";

const NONE = "__none__";

interface ClassDraft {
  code: string;
  name: string;
  nameAr: string;
  usefulLifeMonths: string;
  method: DepreciationMethod;
  decliningFactor: string;
  switchToStraightLine: boolean;
  costAccountId: string;
  accumulatedAccountId: string;
  expenseAccountId: string;
  gainLossAccountId: string;
  active: boolean;
}

const EMPTY_DRAFT: ClassDraft = {
  code: "",
  name: "",
  nameAr: "",
  usefulLifeMonths: "60",
  method: "straight_line",
  decliningFactor: "2",
  switchToStraightLine: true,
  costAccountId: NONE,
  accumulatedAccountId: NONE,
  expenseAccountId: NONE,
  gainLossAccountId: NONE,
  active: true,
};

export interface AssetClassesPageProps {
  canCreate?: boolean;
  canUpdate?: boolean;
  canReadAccounts?: boolean;
}

export function AssetClassesPage({
  canCreate = true,
  canUpdate = true,
  canReadAccounts = true,
}: AssetClassesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const classesQuery = useAssetClasses();
  const accountsQuery = usePostableAccounts(canReadAccounts);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<ClassDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);

  const accountOptions = useMemo(
    () => [
      { value: NONE, label: t("finance.common.choose", "Choose…") },
      ...(accountsQuery.data ?? [])
        .filter((account) => account.is_postable)
        .map((account) => ({ value: account.id, label: `${account.code} · ${account.name}` })),
    ],
    [accountsQuery.data, t],
  );

  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  };

  const startEdit = (assetClass: AssetClass) => {
    setCreating(false);
    setEditingId(assetClass.id);
    setFormError(null);
    setDraft({
      code: assetClass.code,
      name: assetClass.name,
      nameAr: assetClass.name_ar ?? "",
      usefulLifeMonths: String(assetClass.default_useful_life_months),
      method: assetClass.default_method,
      decliningFactor: assetClass.default_declining_factor,
      switchToStraightLine: assetClass.default_switch_to_straight_line,
      costAccountId: assetClass.cost_account_id,
      accumulatedAccountId: assetClass.accumulated_depreciation_account_id,
      expenseAccountId: assetClass.depreciation_expense_account_id,
      gainLossAccountId: assetClass.gain_loss_account_id,
      active: assetClass.active,
    });
  };

  const reset = () => {
    setCreating(false);
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  };

  const createMutation = useIdempotentMutation<AssetClass, AssetClassWrite>({
    mutationFn: (body, key) => createAssetClass(body, key),
    onSuccess: (assetClass) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.assetClasses.all });
      startEdit(assetClass);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { assetClassId: string; body: AssetClassWrite }) =>
      replaceAssetClass(input.assetClassId, input.body),
    onSuccess: (assetClass) => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.assetClasses.all });
      startEdit(assetClass);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const buildBody = (): AssetClassWrite | string => {
    if (draft.code.trim() === "" || draft.name.trim() === "") {
      return t("finance.assetClasses.codeNameRequired", "An asset class needs a code and a name.");
    }
    const life = Number.parseInt(draft.usefulLifeMonths, 10);
    if (Number.isNaN(life) || life < 1 || life > 1200) {
      return t("finance.fixedAssets.lifeInvalid", "Useful life is between 1 and 1200 months.");
    }
    const factor = normalizeDecimal(draft.decliningFactor, { scale: 6 });
    if (factor === null) {
      return t(
        "finance.fixedAssets.factorInvalid",
        "The declining factor is above 1 and at most 5.",
      );
    }
    const accounts = [
      draft.costAccountId,
      draft.accumulatedAccountId,
      draft.expenseAccountId,
      draft.gainLossAccountId,
    ];
    if (accounts.some((account) => account === NONE)) {
      return t(
        "finance.assetClasses.accountsRequired",
        "All four control accounts are required — an unmapped one posts somewhere arbitrary.",
      );
    }
    if (new Set(accounts).size !== accounts.length) {
      return t(
        "finance.assetClasses.accountsMustDiffer",
        "The four control accounts must differ from one another.",
      );
    }
    return {
      code: draft.code.trim(),
      name: draft.name.trim(),
      // The write is a full replace, so an empty box sends null rather than
      // being omitted, which would keep the stored Arabic name.
      name_ar: draft.nameAr.trim() === "" ? null : draft.nameAr.trim(),
      default_useful_life_months: life,
      default_method: draft.method,
      default_declining_factor: factor,
      default_switch_to_straight_line: draft.switchToStraightLine,
      cost_account_id: draft.costAccountId,
      accumulated_depreciation_account_id: draft.accumulatedAccountId,
      depreciation_expense_account_id: draft.expenseAccountId,
      gain_loss_account_id: draft.gainLossAccountId,
      active: draft.active,
    };
  };

  const submit = () => {
    const body = buildBody();
    if (typeof body === "string") {
      setFormError(body);
      return;
    }
    setFormError(null);
    if (creating) createMutation.mutate(body);
    else if (editingId !== null) updateMutation.mutate({ assetClassId: editingId, body });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.assetClasses.title", "Asset classes")}
        description={t(
          "finance.assetClasses.subtitle",
          "Depreciation defaults new assets inherit, and the four control accounts every asset in the class posts through.",
        )}
        actions={
          canCreate ? (
            <Button type="button" onClick={startCreate}>
              <Plus className="size-4" aria-hidden="true" />
              {t("finance.assetClasses.new", "New asset class")}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.assetClasses.listSection", "Classes")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(classesQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.assetClasses.empty", "No asset classes yet.")}
            </p>
          ) : (
            <ul className="flex flex-col divide-y">
              {(classesQuery.data ?? []).map((assetClass) => (
                <li
                  key={assetClass.id}
                  className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span dir="ltr" className="font-mono font-medium">
                      {assetClass.code}
                    </span>
                    <span className="truncate text-muted-foreground">
                      {localizedName(assetClass.name, assetClass.name_ar, i18n.language)}
                    </span>
                  </span>
                  <span className="text-muted-foreground">
                    {t(
                      `finance.fixedAssets.methodOption.${assetClass.default_method}`,
                      assetClass.default_method,
                    )}{" "}
                    ·{" "}
                    {t("finance.assetClasses.lifeMonths", "{{months}} months", {
                      months: assetClass.default_useful_life_months,
                    })}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => startEdit(assetClass)}
                  >
                    {t("finance.assetClasses.edit", "Edit")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {creating || editingId !== null ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>
                {creating
                  ? t("finance.assetClasses.createTitle", "New asset class")
                  : t("finance.assetClasses.editTitle", "Edit {{code}}", { code: draft.code })}
              </h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              id="class-code"
              label={t("finance.assetClasses.code", "Code")}
              value={draft.code}
              onChange={(code) => setDraft((current) => ({ ...current, code }))}
              disabled={!creating}
              hint={creating ? undefined : t("finance.assetClasses.codeFrozen", "Immutable.")}
              ltr
              maxLength={32}
            />
            <TextField
              id="class-name"
              label={t("finance.assetClasses.name", "Name")}
              value={draft.name}
              onChange={(name) => setDraft((current) => ({ ...current, name }))}
              disabled={!creating && !canUpdate}
              className="lg:col-span-2"
              maxLength={256}
            />
            <TextField
              id="class-name-ar"
              label={t("finance.assetClasses.nameAr", "Name (Arabic)")}
              value={draft.nameAr}
              onChange={(nameAr) => setDraft((current) => ({ ...current, nameAr }))}
              disabled={!creating && !canUpdate}
              className="lg:col-span-2"
              maxLength={255}
              hint={t(
                "finance.assetClasses.nameArHint",
                "Optional. Shown to operators working in Arabic.",
              )}
            />
            <TextField
              id="class-life"
              label={t("finance.assetClasses.defaultLife", "Default useful life (months)")}
              value={draft.usefulLifeMonths}
              onChange={(usefulLifeMonths) =>
                setDraft((current) => ({ ...current, usefulLifeMonths }))
              }
              disabled={!creating && !canUpdate}
              ltr
            />
            <SelectField
              id="class-method"
              label={t("finance.assetClasses.defaultMethod", "Default method")}
              value={draft.method}
              onValueChange={(method) =>
                setDraft((current) => ({ ...current, method: method as DepreciationMethod }))
              }
              options={DEPRECIATION_METHODS.map((value) => ({
                value,
                label: t(`finance.fixedAssets.methodOption.${value}`, value),
              }))}
              disabled={!creating && !canUpdate}
            />
            <DecimalField
              id="class-factor"
              label={t("finance.assetClasses.defaultFactor", "Default declining factor")}
              value={draft.decliningFactor}
              onChange={(decliningFactor) =>
                setDraft((current) => ({ ...current, decliningFactor }))
              }
              decimal={{ scale: 6 }}
              disabled={!creating && !canUpdate}
              invalidMessage={t(
                "finance.fixedAssets.factorInvalid",
                "The declining factor is above 1 and at most 5.",
              )}
            />
            <SelectField
              id="class-cost-account"
              label={t("finance.assetClasses.costAccount", "Cost account")}
              value={draft.costAccountId}
              onValueChange={(costAccountId) =>
                setDraft((current) => ({ ...current, costAccountId }))
              }
              options={accountOptions}
              disabled={!canReadAccounts || (!creating && !canUpdate)}
            />
            <SelectField
              id="class-accumulated-account"
              label={t(
                "finance.assetClasses.accumulatedAccount",
                "Accumulated depreciation account",
              )}
              value={draft.accumulatedAccountId}
              onValueChange={(accumulatedAccountId) =>
                setDraft((current) => ({ ...current, accumulatedAccountId }))
              }
              options={accountOptions}
              disabled={!canReadAccounts || (!creating && !canUpdate)}
            />
            <SelectField
              id="class-expense-account"
              label={t("finance.assetClasses.expenseAccount", "Depreciation expense account")}
              value={draft.expenseAccountId}
              onValueChange={(expenseAccountId) =>
                setDraft((current) => ({ ...current, expenseAccountId }))
              }
              options={accountOptions}
              disabled={!canReadAccounts || (!creating && !canUpdate)}
            />
            <SelectField
              id="class-gain-loss-account"
              label={t("finance.assetClasses.gainLossAccount", "Gain or loss on disposal account")}
              value={draft.gainLossAccountId}
              onValueChange={(gainLossAccountId) =>
                setDraft((current) => ({ ...current, gainLossAccountId }))
              }
              options={accountOptions}
              disabled={!canReadAccounts || (!creating && !canUpdate)}
            />
            <label className="flex items-center gap-2 text-sm sm:col-span-2 lg:col-span-3">
              <Checkbox
                checked={draft.switchToStraightLine}
                disabled={!creating && !canUpdate}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, switchToStraightLine: checked === true }))
                }
              />
              {t(
                "finance.assetClasses.switchDefault",
                "Declining balance switches to straight line once it charges more",
              )}
            </label>
            <label className="flex items-center gap-2 text-sm sm:col-span-2 lg:col-span-3">
              <Checkbox
                checked={draft.active}
                disabled={!creating && !canUpdate}
                onCheckedChange={(checked) =>
                  setDraft((current) => ({ ...current, active: checked === true }))
                }
              />
              {t("finance.assetClasses.activeLabel", "Active")}
            </label>

            {formError === null ? null : (
              <div className="sm:col-span-2 lg:col-span-3">
                <ProblemBanner>{formError}</ProblemBanner>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-3">
              {creating || canUpdate ? (
                <Button
                  type="button"
                  loading={createMutation.isPending || updateMutation.isPending}
                  onClick={submit}
                >
                  {creating
                    ? t("finance.assetClasses.create", "Create class")
                    : t("finance.assetClasses.save", "Save class")}
                </Button>
              ) : null}
              <Button type="button" variant="ghost" onClick={reset}>
                {t("actions.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
